import { Button, ButtonVariant, DataSourceMode, DataSourceModeChangeEvent, EditRowDecorator, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, MultiButton, Snackbar, TableContentsChangedEvent, TableRowMode } from "@mcleod/components";
import { AutogenLayoutARCallRecord } from "./autogen/AutogenLayoutARCallRecord";
import { LMEGeneralSettings, LMEUserSettings } from "@mcleod/general";
import { Alignment, Api, Currency, DatePart, DateUtil, LogManager, Model, ModelRow } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { CallRecordOpenItemTable } from "./CallRecordOpenItemTable";
import { ContactUtil } from "@mcleod/dispatch/src/ContactUtil";
import { ImageName } from "@mcleod/images";
import { ReprintFreightBill } from "./ReprintFreightBill";

interface ARCallRecordSlideoutProps {
    customerId: string;
    customerName: string;
    customerActionCode?: string;
    customerActionCodeDescr?: string;
    defaultNextActionCode?: string; // Default next action values are used on new call records.
    defaultNextActionDays?: number; // They come from the customer's next action code's core file.
    defaultNextActionDescr?: string;
    discussedItems?: ModelRow[];
    callRecordId?: string;
    doAfterSlideOut?: (success: boolean, callRecordId?: string) => void;
}

const log = LogManager.getLogger("lme.ar.ARCallRecord");
const CALL_REC_OPEN_ITEM_ENDPOINT = "lme/ar/call-rec-open-item";

export class ARCallRecord extends AutogenLayoutARCallRecord {
    public actionButton: Button;
    private defaultBillDueDays: number;
    private openInSlideout: boolean = false;

    override onLoad() {
        const arControl = LMEGeneralSettings.getSingleton().getARControl();
        this.defaultBillDueDays = arControl["bill_due_days"] ?? 0;
        this.layoutCallRecordOpenItemTable.defaultBillDueDays = arControl["bill_due_days"] ?? 0;
        this.layoutCallRecordOpenItemTable.tableCallRecOpenItem.addContentsChangedListener((event: TableContentsChangedEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.ADD) {
                const rowCount = event.getTable().rowCount;
                this.textboxCustomerCode.enabled = !this.openInSlideout && rowCount === 0;
            }
        });

        this.attachmentAttachment.setAttachmentWithUI = async (file: File) => {
            const maxSize = this.attachmentAttachment.maxSize;
            const fileName = file.name.toLowerCase();
            const allowedExtensions = [".apng", ".avif", ".svg", ".webp", ".jpg", ".jpeg", ".png", ".gif", ".pdf"];
            const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
            if (!hasValidExtension) {
                return this.attachmentAttachment.showAdhocTooltip("The file must be an image or PDF.", { shaking: true, timeout: 5000 });
            } else if (maxSize != null && file.size > maxSize) {
                return this.attachmentAttachment.showAdhocTooltip("The file is too big to attach. The file must be smaller than " + this.attachmentAttachment.maxSize / (1024 * 1024) + "MB.", { shaking: true, timeout: 5000 });
            } else {
                this.attachmentAttachment.setFile(file);
            }
        }
    }

    // START - Slideout logic
    public static showSlideout(props: ARCallRecordSlideoutProps) {
        const layout = Layout.getLayout("lme/ar/ARCallRecord") as ARCallRecord;
        let saveButton: MultiButton
        const title = layout.setUpSlideoutTitle(props);
        let success = false;
        const buttonReprintFreightBill = layout.createReprintFreightBillButton();
        const buttonReprintBalanceDue = layout.createReprintBalanceDueButton();

        const decorator = new EditRowDecorator({
            layout,
            width: '75%',
            fillHeight: true,
            title: title,
            displayOverlay: true,
            overlayProps: { greyedBackground: true },
            addlComponents: [buttonReprintFreightBill, buttonReprintBalanceDue],
            layoutLoadListeners: async () => {
                layout.openInSlideout = true;
                layout.setDefaultValuesForSlideout(props);
                await layout.populateDiscussedItems(props);
            },
            doAfterSlideIn: (decorator: EditRowDecorator) => {
                saveButton = decorator.multiButton;
            },
            onSave: async () => {
                saveButton.busy = true;
                try {
                    await layout.mainDataSource.post();
                    success = true;
                } catch (error) {
                    success = false;
                    log.error("Error posting AR Call Record:", error);
                    CommonDialogs.showError(error);
                } finally {
                    saveButton.busy = false;
                }
            },
            onClose: () => {
                decorator.slideOut();
            },
            doAfterSlideOut: () => {
                const callRecordId = layout.mainDataSource.activeRow?.getString("id", null);
                props.doAfterSlideOut?.(success, callRecordId);
            }
        });
    }

    setUpSlideoutTitle(props: ARCallRecordSlideoutProps) {
        const suffix = " - " + (props.customerName ?? props.customerId);
        return "Call Record Details" + suffix;
    }

    setDefaultValuesForSlideout(props: ARCallRecordSlideoutProps) {
        this.mainDataSource.clear();
        if (props.callRecordId == null) {
            this.mainDataSource.mode = DataSourceMode.ADD;
            this.mainDataSource.createBlankRow().then(() => {
                this.mainDataSource.activeRow?.setValueAndLookupModelData("customer_id", props.customerId, {
                    "id": props.customerId, "name": props.customerName
                });
                this.mainDataSource.activeRow?.setValueAndLookupModelData("action_code_id", props.customerActionCode, {
                    "id": props.customerActionCode, "descr": props.customerActionCodeDescr
                });
                this.mainDataSource.activeRow?.setValueAndLookupModelData("nxt_action_code", props.defaultNextActionCode, {
                    "id": props.defaultNextActionCode, "descr": props.defaultNextActionDescr
                });

                if (props.defaultNextActionDays != null && props.defaultNextActionDays >= 0) {
                    this.mainDataSource.activeRow?.set("nxt_action_date", DateUtil.dateAdd(DatePart.DAY, new Date(), props.defaultNextActionDays));
                }

                this.textboxContacted.enabled = true;
            });
        } else {
            this.mainDataSource.search({ id: props.callRecordId }).then(response => {
                this.mainDataSource.setRowsAndMode(DataSourceMode.UPDATE, response?.modelRows as ModelRow[]);
            });
        }
        this.textboxCustomerCode.enabled = false;
    }

    // Populates the Items Discussed table with the selected rows from the Collections table.
    // This method is only called when using the Add Call button on the Collections screen.
    async populateDiscussedItems(props: ARCallRecordSlideoutProps) {
        let discussedItems = props.discussedItems;
        if (discussedItems && discussedItems.length > 0) {
            if (discussedItems.some(item => item.getBoolean("contains_multiple_items"))) {
                // Some selected items summarize multiple open items, so we need to fetch the full details from the API
                const response = await Api.post("lme/ar/convert-open-items", { open_items: discussedItems });
                if (response?.data?.[0]?.call_record_open_items) {
                    discussedItems = response.data[0].call_record_open_items;
                }
            }

            const callRecordOpenItemTable = this.layoutCallRecordOpenItemTable;
            if (callRecordOpenItemTable && callRecordOpenItemTable instanceof CallRecordOpenItemTable) {
                callRecordOpenItemTable.parentCustomerId = props.customerId;
                await callRecordOpenItemTable.getBridgeCustomerIds();
                for (const openItem of discussedItems) {
                    const modelRow = this.mapOpenItemToModelRow(openItem);
                    callRecordOpenItemTable.tableCallRecOpenItem.addRow(modelRow, { mode: TableRowMode.NONE }, { display: true, addToData: true });
                }
            }
        }
    }

    // Transforms an open item (either a ModelRow or a plain object) into a ModelRow suitable for the Call Record Open Item table.
    // This method is only called when using the Add Call button on the Collections screen to create a new call record.
    private mapOpenItemToModelRow(openItem: any): ModelRow {
        let data: any;
        let open_item_id: string;
        let invoice_no_string: string;

        if (openItem instanceof ModelRow) {
            data = openItem.data;
            open_item_id = openItem.getString("id");
            invoice_no_string = openItem.getString("invoice_no");
        } else {
            data = openItem;
            open_item_id = openItem.open_item_id;
            invoice_no_string = openItem.invoice_no_string;
        }

        const { amount, bill_date, consignee_refno, customer_id, days_past_due, order_id, record_type, reference_number, source, customer_summary_billing } = data;
        const customer_bill_due_days = data.customer_bill_due_days > -1 ? data.customer_bill_due_days : this.defaultBillDueDays;
        const modelRow = new ModelRow(CALL_REC_OPEN_ITEM_ENDPOINT);

        modelRow.updateCurrency("amount", new Currency(amount));
        modelRow.set("bill_date", bill_date);
        modelRow.set("bill_due_days", customer_bill_due_days);
        modelRow.setValueAndLookupModelData("consignee_refno", consignee_refno, { consignee_refno: consignee_refno });
        modelRow.set("customer_id", customer_id);
        modelRow.set("days_past_due", days_past_due);
        modelRow.set("entered_date", new Date());
        modelRow.setValueAndLookupModelData("invoice_no_string", invoice_no_string, { invoice_no_string: invoice_no_string });
        modelRow.set("open_item_id", open_item_id);
        modelRow.setValueAndLookupModelData("order_id", order_id, { order_id: order_id });
        modelRow.set("record_type", record_type);
        modelRow.setValueAndLookupModelData("reference_number", reference_number, { reference_number: reference_number });
        modelRow.set("source", source);
        modelRow.set("summary_billing", customer_summary_billing);
        return modelRow;
    }
    // END - Slideout logic

    // START - Slideout additional buttons logic
    createReprintFreightBillButton(): Button {
        return new Button({
            id: "buttonReprintFreightBill",
            tooltip: "Reprint Freight Bill",
            variant: ButtonVariant.round,
            borderWidth: 0,
            rowBreak: false,
            imageName: ImageName.reprintFreightBill,
            imageHeight: 32,
            imageWidth: 32,
            onClick: async () => {
                await this.openReprintSlideout(false);
            }
        });
    }

    createReprintBalanceDueButton(): Button {
        return new Button({
            id: "buttonReprintBalanceDue",
            tooltip: "Reprint Balance Due",
            variant: ButtonVariant.round,
            borderWidth: 0,
            rowBreak: false,
            imageName: ImageName.balanceDue,
            imageHeight: 32,
            imageWidth: 32,
            onClick: async () => {
                await this.openReprintSlideout(true);
            }
        });
    }

    async openReprintSlideout(isBalanceDue: boolean) {
        const selectedRow = this.layoutCallRecordOpenItemTable.tableCallRecOpenItem.selectedRow;
        const modelRow = selectedRow?.data as ModelRow;
        if (modelRow != null && !selectedRow.isQuickAddRow()) {
            const includeOnlyInvoices = isBalanceDue && await CommonDialogs.showYesNo("Include only invoice(s) with balance?");
            const sendToImaging = await CommonDialogs.showYesNo("Send report to Imaging?");
            if (sendToImaging == null) {
                return;
            }

            const reprintFreightBillProps = {
                isSummaryBill: modelRow.getString("summary_billing") === "Y", // Possible values are "N" for "Regular", "Y" for "Summary", and "D" for "Dedicated"
                invoiceNumber: modelRow.getString("invoice_no_string"),
                orderId: modelRow.getString("order_id"),
                customerId: modelRow.getString("customer_id"),
                sendToImaging: sendToImaging,
                billDate: modelRow.getDate("bill_date"),
                billType: modelRow.getString("record_type", "I"),
                isBalanceDue: isBalanceDue,
                includeOnlyInvoices: includeOnlyInvoices,
                width: "100%",
                transitionOptions: {
                    direction: Alignment.BOTTOM
                }
            };

            ReprintFreightBill.openSlideout(reprintFreightBillProps);
        } else {
            Snackbar.showWarningSnackbar("Please select an open item to reprint the " + (isBalanceDue ? "balance due." : "freight bill."));
        }
    }
    // END - Slideout additional buttons logic

    sourceArCallRecordAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.ADD) {
            this.mainDataSource.activeRow?.set("call_date", new Date());
            this.layoutCallRecordOpenItemTable.callDate = this.mainDataSource.activeRow?.getDate("call_date", new Date());
            this.mainDataSource.activeRow?.setValueAndLookupModelData("caller_user_id", LMEUserSettings.getUserId(),
                LMEUserSettings.getUserLookupModelData()
            );
            this.textboxContacted.enabled = this.mainDataSource.activeRow?.getString("customer_id", null) != null;
        }
    }

    async sourceArCallRecordOnDisplay() {
        const customerId = this.mainDataSource.activeRow?.getString("customer_id", null);
        if (customerId) {
            this.layoutCallRecordOpenItemTable.parentCustomerId = customerId;
            await this.layoutCallRecordOpenItemTable.getBridgeCustomerIds();
        }

        const callDate = this.mainDataSource.activeRow?.getDate("call_date", new Date());
        if (callDate) {
            this.layoutCallRecordOpenItemTable.callDate = callDate;
        }

        const contactName = this.mainDataSource.activeRow?.getString("contact_name");
        if (contactName != null) {
            this.mainDataSource.activeRow?.setValueAndLookupModelData("contact_name", contactName, {
                "name": contactName
            });
        }
    }

    async textboxCallActionCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selectedRow = event.selection;
            const defaultNextActionCode = selectedRow.getString("nxt_action_code_id", null);
            const defaultNextActionDays = selectedRow.getNumber("nxt_action_days", -1);
            try {
                const nextActionCodeRow = await Model.searchSingleRecord("lme/ar/action-code", { id: defaultNextActionCode });

                if (nextActionCodeRow != null) {
                    this.mainDataSource.activeRow?.setValueAndLookupModelData("nxt_action_code", defaultNextActionCode, {
                        "id": defaultNextActionCode,
                        "descr": nextActionCodeRow.getString("descr", null)
                    });
                }
            } catch (error) {
                log.error(`Error fetching next action code details for code ${defaultNextActionCode}. Could not set lookup model data.`, error);
                this.mainDataSource.activeRow?.set("nxt_action_code", defaultNextActionCode);
            }

            if (defaultNextActionDays >= 0) {
                const callActionDate = this.mainDataSource.activeRow?.getDate("call_date", new Date());
                this.mainDataSource.activeRow?.set("nxt_action_date", DateUtil.dateAdd(DatePart.DAY, callActionDate, defaultNextActionDays));
            }
        }
    }

    textboxContactedBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.parent_row_type = "C";
        const customerId = this.mainDataSource.activeRow.getString("customer_id", null);
        if (customerId) {
            event.filter.parent_row_id = customerId;
        }
    }

    textboxContactedOnLookupModelSelection(event: LookupModelSelectionEvent) {
        ContactUtil.layoutContactNameLookupModelSelection(event, {
            contactNameField: "contact_name",
            contactPhoneField: "phone",
            contactEmailField: "email",
            contactTitleField: "title",
            nameTextbox: this.textboxContacted,
            layout: this
        });
    }

    textboxCustomerCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selectedRow = event.selection;
            const customerId = selectedRow.getString("id", null);
            this.layoutCallRecordOpenItemTable.parentCustomerId = customerId;
            this.layoutCallRecordOpenItemTable.getBridgeCustomerIds();
        } else {
            this.mainDataSource.activeRow?.setValueAndLookupModelData("contact_name", null, null);
            this.mainDataSource.activeRow?.set("email", null);
            this.mainDataSource.activeRow?.set("phone", null);
            this.mainDataSource.activeRow?.set("title", null);
        }
        this.textboxContacted.enabled = event.hasSelection();
    }
}
